"use client";

import * as React from "react";

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

export const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-2xl font-semibold mb-3">{title}</h2>
    <div className="space-y-3 text-justify">{children}</div>
  </section>
);

const LAST_UPDATED_ISO = "2025-07-01";

export const TermsContent: React.FC<{ withPageTitle?: boolean }> = ({
  withPageTitle = true,
}) => {
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString("pt-BR");

  return (
    <div>
      {withPageTitle && (
        <>
          <h1 className="text-3xl font-bold mb-4">
            Termos de Uso da Plataforma Nacional GO
          </h1>
          <p className="text-sm text-n-text-neutral mb-8">
            Última atualização: {lastUpdated}
          </p>
        </>
      )}

      <Section title="1. Apresentação">
        <p>
          Estes Termos de Uso (“Termo”) regulam o acesso e a utilização da
          plataforma digital da Nacional GO por usuários, operada por NACIONAL
          GO TECNOLOGIA LTDA, inscrita no CNPJ sob nº 46.951.207/0001-42, com
          sede rodovia José Carlos Daux, 4150, Primavera Office, Sala 102, Saco
          Grande, Florianópolis/SC, CEP: 88.032-005, doravante denominada
          "Plataforma".
        </p>
        <p>
          A Nacional GO disponibiliza serviços digitais para intermediação e
          gestão de transporte de veículos automotores em todo o território
          nacional, conectando clientes a transportadores parceiros
          credenciados, de forma eficiente, segura e tecnológica.
        </p>
        <p>
          Ao contratar os serviços, o Usuário declara ter lido, compreendido e
          aceito integralmente os presentes Termos de Uso.
        </p>
      </Section>

      <Section title="2. Definições">
        <ul className="list-disc pl-5">
          <li>
            <strong>Plataforma:</strong> sistema digital da Nacional GO para
            contratação de transporte e serviços relacionados.
          </li>
          <li>
            <strong>Usuário:</strong> pessoa física ou jurídica que contrata os
            serviços oferecidos pela Plataforma.
          </li>
          <li>
            <strong>Transportador:</strong> parceiro credenciado responsável
            pela execução física do transporte.
          </li>
          <li>
            <strong>Veículo:</strong> automóvel, motocicleta ou qualquer meio de
            transporte terrestre objeto dos serviços contratados.
          </li>
          <li>
            <strong>Serviços:</strong> atividades de intermediação,
            roteirização, contratação de transporte e oferta de serviços
            adicionais pela Plataforma.
          </li>
        </ul>
      </Section>

      <Section title="3. Objeto dos Termos">
        <p>O presente Termo disciplina:</p>
        <ul className="list-disc pl-5">
          <li>As condições de uso da Plataforma;</li>
          <li>
            A contratação dos serviços de transporte de veículos e serviços
            acessórios;
          </li>
          <li>As responsabilidades, direitos e obrigações entre as partes.</li>
        </ul>
      </Section>

      <Section title="4. Aceitação dos Termos">
        <p>
          O aceite destes Termos de Uso é condição essencial para utilização dos
          serviços.
        </p>
        <p>O aceite se dará:</p>
        <ul className="list-disc pl-5">
          <li>
            Pelo clique na opção "Li e Aceito" no momento da finalização do
            pedido na Plataforma; ou
          </li>
          <li>
            Pela conclusão do pagamento em vendas intermediadas por atendimento
            comercial.
          </li>
        </ul>
        <p>
          A contratação presume ciência e concordância integral aos presentes
          Termos.
        </p>
      </Section>

      <Section title="5. Serviços Disponíveis">
        <p>A Nacional GO oferece:</p>
        <ul className="list-disc pl-5">
          <li>
            <strong>Intermediação de Transporte de Veículos:</strong> conexão do
            Usuário a transportadores parceiros.
          </li>
          <li>
            <strong>Roteirização Inteligente:</strong> combinação de diferentes
            transportadores, otimizando o prazo estimado.
          </li>
          <li>
            <strong>Serviços Adicionais:</strong> seguros, vistoria cautelar,
            armazenagem, transporte de itens pessoais e outros, conforme
            contratação específica.
          </li>
        </ul>
        <p>Modalidades de transporte:</p>
        <ul className="list-disc pl-5">
          <li>
            <strong>Convencional:</strong> prazo estimado padrão.
          </li>
          <li>
            <strong>Prioritária ou Expressa:</strong> prazos reduzidos, mediante
            contratação e pagamento adicional.
          </li>
        </ul>
      </Section>

      <Section title="6. Coleta, Entrega e Serviços de Guincho">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>6.1 Local e Comunicação:</strong> Endereços em áreas de
            difícil acesso podem gerar ajuste de valor.
          </li>
          <li>
            <strong>6.2 Agendamento Padrão:</strong> Data definida, sem horário
            exato.
          </li>
          <li>
            <strong>6.3 Agendamento com Hora Marcada:</strong> Serviço opcional
            com custo adicional.
          </li>
          <li>
            <strong>6.4 Custos de Espera:</strong> R$ 80,00/hora de espera.
            Deslocamentos extras podem gerar custos.
          </li>
          <li>
            <strong>6.5 Tentativas Frustradas:</strong> Cobrança dos custos de
            deslocamento e hora parada.
          </li>
          <li>
            <strong>6.6 Ajuste de Valores:</strong> Para áreas de difícil
            acesso.
          </li>
          <li>
            <strong>6.7 Coletas de Retorno:</strong> Serviço adicional, com
            valor e prazo próprios.
          </li>
        </ul>
      </Section>

      <Section title="7. Seguro">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Cobertura Básica:</strong> roubo, furto qualificado,
            colisões e acidentes.
          </li>
          <li>
            <strong>Cobertura Total (opcional):</strong> cobre qualquer avaria.
          </li>
          <li>
            As condições específicas estão nas políticas públicas da Plataforma.
          </li>
        </ul>
      </Section>

      <Section title="8. Prazos de Entrega e Penalidades">
        <p>
          O prazo estimado é informado na contratação. Variações podem ocorrer.
        </p>
        <p>
          Se o atraso for superior a 5 dias úteis, o cliente recebe 10% de
          abatimento.
        </p>
        <p>
          <strong>Atenção:</strong> Não há responsabilidade por despesas com
          deslocamento, aluguel de veículos ou danos indiretos.
        </p>
      </Section>

      <Section title="9. Custos Adicionais por Veículos Inoperantes">
        <p>Veículos inoperantes geram taxas extras por:</p>
        <ul className="list-disc pl-5">
          <li>Manuseio especializado;</li>
          <li>Uso de dispositivos ou guindastes;</li>
          <li>Realocação de rota, espera ou serviços extras.</li>
        </ul>
        <p>
          <strong>9.1 Interrupção da Operação:</strong> Se houver pane durante o
          transporte, o cliente arca com custos de:
        </p>
        <ul className="list-disc pl-5">
          <li>Retirada emergencial;</li>
          <li>Carga e descarga extras;</li>
          <li>Deslocamento e espera adicionais.</li>
        </ul>
      </Section>

      <Section title="10. Retirada e Armazenagem">
        <ul className="list-disc pl-5">
          <li>
            <strong>10.1 Prazo:</strong> Retirada até 1 dia útil após chegada.
            Após isso, R$35,00/dia.
          </li>
          <li>
            <strong>10.2 Prazo Flexível:</strong> Pode ser contratado até 10
            dias corridos sem taxa.
          </li>
          <li>
            <strong>10.3 Veículos Abandonados:</strong> Após 30 dias, são
            considerados abandonados, gerando despesas e possíveis medidas
            judiciais.
          </li>
        </ul>
      </Section>

      <Section title="11. Transporte de Veículos de Terceiros">
        <p>
          O cliente deve ser proprietário ou ter autorização válida. A
          Plataforma não se responsabiliza por disputas de titularidade.
        </p>
      </Section>

      <Section title="12. Cessão de Conta">
        <p>
          O acesso é pessoal e intransferível. Ceder ou compartilhar login
          resulta em suspensão e responsabilização.
        </p>
      </Section>

      <Section title="13. Condições do Veículo e Avarias">
        <p>A cobertura não se aplica a:</p>
        <ul className="list-disc pl-5">
          <li>Veículos sinistrados;</li>
          <li>Veículos inoperantes sem contratação de transporte especial;</li>
          <li>Veículos com conservação precária.</li>
        </ul>
      </Section>

      <Section title="14. Transporte de Itens Pessoais">
        <ul className="list-disc pl-5">
          <li>Permitido apenas mediante contratação adicional.</li>
          <li>Sem cobertura de seguro, salvo contratação específica.</li>
          <li>
            É proibido transportar itens ilícitos, perigosos ou de alto valor.
          </li>
        </ul>
      </Section>

      <Section title="15. Suporte e Atendimento ao Cliente">
        <ul className="list-disc pl-5">
          <li>Prazo de resposta inicial: até 2 dias úteis.</li>
          <li>Sinistros: abertura em até 2 dias úteis após o fato.</li>
          <li>Atendimento preferencialmente digital.</li>
        </ul>
      </Section>

      <Section title="16. Cancelamento e Reembolso">
        <ul className="list-disc pl-5">
          <li>
            <strong>Antes da coleta:</strong> Reembolso integral.
          </li>
          <li>
            <strong>Após início da operação:</strong> Reembolso proporcional aos
            custos.
          </li>
          <li>
            <strong>Falha da Nacional GO:</strong> Cliente pode optar por
            reembolso total ou crédito.
          </li>
        </ul>
      </Section>

      <Section title="17. Responsabilidade por Danos, Avarias, Atrasos ou Perdas">
        <p>
          A Nacional GO é responsável solidariamente com os transportadores. A
          cobertura depende da contratação de seguro.
        </p>
        <p>
          Não se responsabiliza por força maior, danos indiretos ou omissões
          fora dos limites contratados.
        </p>
      </Section>

      <Section title="18. Faturamento">
        <p>
          Recibo e nota fiscal são emitidos após a conclusão do serviço. O
          faturamento refere-se à intermediação da Nacional GO.
        </p>
      </Section>

      <Section title="19. Uso Indevido da Plataforma, Penalidades e Sanções">
        <p>
          Fraudes, ocultação de informações ou transporte de itens ilícitos
          resultam em:
        </p>
        <ul className="list-disc pl-5">
          <li>Suspensão imediata;</li>
          <li>Retenção de valores até apuração;</li>
          <li>Indenização à Nacional GO e/ou clientes;</li>
          <li>Cooperação com autoridades judiciais.</li>
        </ul>
      </Section>

      <Section title="20. Integridade e Anticorrupção">
        <p>
          As partes declaram cumprir as leis anticorrupção (Lei nº 12.846/2013).
          É vedado oferecer vantagens indevidas a agentes públicos ou privados.
        </p>
      </Section>

      <Section title="21. Propriedade Intelectual">
        <p>
          Todo o conteúdo da Plataforma pertence à Nacional GO. É proibida
          reprodução, cópia ou distribuição sem autorização.
        </p>
      </Section>

      <Section title="22. Modificações dos Termos de Uso">
        <p>
          Os termos podem ser alterados a qualquer momento. A continuidade do
          uso implica aceitação automática.
        </p>
      </Section>

      <Section title="23. Foro e Legislação Aplicável">
        <p>
          Aplica-se a legislação brasileira. Foro eleito: Comarca de
          Florianópolis/SC.
        </p>
      </Section>

      <Section title="24. Disposições Finais">
        <p>Este Termo integra, junto com:</p>
        <ul className="list-disc pl-5">
          <li>Política de Privacidade;</li>
          <li>Política de Cancelamento;</li>
          <li>Condições Gerais de Seguro;</li>
          <li>Outros documentos públicos da Plataforma.</li>
        </ul>
        <p>
          O Usuário declara ter lido, compreendido e aceito integralmente estes
          termos.
        </p>
      </Section>
    </div>
  );
};
